import { z } from "zod";

import { ACTOR_TYPE_VALUES, type Actor } from "./actor";
import { objectIdStringSchema } from "./object-id";

// A domain event states that a business action completed; names are past tense.
// Events carry identifiers, not business data -- consumers read the current record.
export const DOMAIN_EVENT_NAME_VALUES = [
  "bank_account.verified",
  "behavior.created",
  "bill.approved",
  "bill.created",
  "bill.paid",
  "bill.synced",
  "bill_policy.updated",
  "budget.created",
  "card_transaction.approved",
  "card_transaction.receipt_added",
  "card_transaction.synced",
  "company.app_installed",
  "company.app_uninstalled",
  "company.settings_updated",
  "custom_field.created",
  "expense_reimbursement.approved",
  "expense_reimbursement.created",
  "hazard.created",
  "incident.created",
  "injury_source.created",
  "injury_type.created",
  "integration_sync.pull_completed",
  "job.address_updated",
  "job.per_diem_rates_updated",
  "job_progress_entry.created",
  "per_diem_rate.created",
  "per_diem_rate.updated",
  "per_diems.generated",
  "permission_group.saved",
  "reimbursement_category.saved",
  "review.submitted",
  "review_cycle.launched",
  "review_packet.acknowledged",
  "review_packet.shared",
  "review_template.updated",
  "safety_checklist_template.created",
  "safety_meeting.completed",
  "safety_meeting_template.created",
  "safety_template_job_assignment.created",
  "third_party_card.created",
  "third_party_card.updated",
  "vendor.created",
  "vendor.payment_details_updated",
  "writeup.created",
  "writeup.signed",
  "writeup_template.updated",
  "year_end_logical_warning.updated",
] as const;

export type DomainEventName = (typeof DOMAIN_EVENT_NAME_VALUES)[number];

export type DomainEvent = {
  company_id: string;
  name: DomainEventName;
  object_type: string;
  object_id: string;
  actor: Actor;
  occurred_at: number;
};

export const DomainEventZodSchema = z.object({
  company_id: objectIdStringSchema,
  name: z.enum(DOMAIN_EVENT_NAME_VALUES),
  object_type: z.string().min(1),
  object_id: objectIdStringSchema,
  actor: z.object({
    id: z.string().min(1),
    type: z.enum(ACTOR_TYPE_VALUES),
  }),
  occurred_at: z.number(),
}) satisfies z.ZodType<DomainEvent>;
